import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import InlineForm from './InlineForm';

// An in-page form for creating or editing an IAM policy: a named grant that
// binds one user group to a set of services its members may use. When `policy`
// is provided the form starts in edit mode (prefilled) and saves via PATCH;
// otherwise it creates via POST. The pickable list covers both the provider
// service catalogue and the console areas (Console home, Providers, IAM,
// Contract management, Price management, Billing), which are granted the same
// way.
export default function PolicyForm({ groups = [], policy, onClose, onSaved }) {
  const editing = !!policy;
  const [name, setName] = useState(policy?.name || '');
  const [description, setDescription] = useState(policy?.description || '');
  const [groupId, setGroupId] = useState(
    policy?.groupId ? String(policy.groupId) : String(groups[0]?.id || ''),
  );
  const [serviceIds, setServiceIds] = useState(() =>
    (policy?.services || []).map((s) => s.id),
  );
  const [services, setServices] = useState([]);
  // The dropdown's current choice, staged until + is pressed.
  const [toAdd, setToAdd] = useState('');
  const [loadingServices, setLoadingServices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoadingServices(true);
    api
      .getPolicyOptions()
      .then((list) => setServices(list || []))
      .catch(() => setServices([]))
      .finally(() => setLoadingServices(false));
  }, []);

  // Services are granted one at a time: pick from the dropdown and press +,
  // press − on a row to drop it. Nothing is persisted until Save.
  const serviceById = useMemo(
    () => Object.fromEntries(services.map((s) => [s.id, s])),
    [services]
  );
  const available = useMemo(
    () => services.filter((s) => !serviceIds.includes(s.id)),
    [services, serviceIds]
  );

  // Keep the dropdown on a valid choice as the available list shrinks.
  useEffect(() => {
    setToAdd((cur) =>
      cur && available.some((s) => s.id === cur) ? cur : available[0]?.id || ''
    );
  }, [available]);

  const addService = () => {
    if (!toAdd || serviceIds.includes(toAdd)) return;
    setServiceIds((ids) => [...ids, toAdd]);
  };

  const removeService = (id) =>
    setServiceIds((ids) => ids.filter((x) => x !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Policy name is required');
      return;
    }
    if (!groupId) {
      setError('Select a user group');
      return;
    }
    const payload = {
      name: name.trim(),
      description: description.trim(),
      groupId: Number(groupId),
      serviceIds,
    };
    setSubmitting(true);
    setError(null);
    try {
      const saved = editing
        ? await api.updatePolicy(policy.id, payload)
        : await api.createPolicy(payload);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <InlineForm
      title={editing ? 'Edit policy' : 'Create policy'}
      onSubmit={submit}
      onCancel={onClose}
      submitting={submitting}
      submitLabel={editing ? 'Save changes' : 'Create policy'}
      error={error}
    >
      <label className="field">
        <span>Policy name</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Compute operators"
          autoFocus
          required
        />
      </label>

      <label className="field">
        <span>Description (optional)</span>
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What does this policy allow?"
        />
      </label>

      <label className="field">
        <span>User group</span>
        <select
          className="input"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          disabled={groups.length === 0}
          required
        >
          {groups.length === 0 ? (
            <option value="">No user groups available</option>
          ) : (
            groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))
          )}
        </select>
      </label>

      <div className="field">
        <span>Allowed services</span>

        {/* Chosen services, each removable. */}
        {serviceIds.length > 0 ? (
          <ul className="picklist">
            {serviceIds.map((id) => {
              const svc = serviceById[id];
              return (
                <li key={id} className="picklist__item">
                  <span className="picklist__main">{svc?.name || id}</span>
                  <span className="picklist__meta">
                    {svc?.category || 'unknown service'}
                  </span>
                  <button
                    type="button"
                    className="btn btn--danger-ghost btn--sm picklist__btn"
                    onClick={() => removeService(id)}
                    aria-label={`Remove ${svc?.name || id}`}
                    title="Remove this service"
                  >
                    −
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="field__hint">
            No services yet — pick one below and press <strong>+</strong>.
          </p>
        )}

        {/* Add a service: only those not already granted are offered. */}
        <div className="field-with-btn">
          <select
            className="input"
            value={toAdd}
            onChange={(e) => setToAdd(e.target.value)}
            disabled={loadingServices || available.length === 0}
          >
            {available.length === 0 ? (
              <option value="">
                {loadingServices
                  ? 'Loading services…'
                  : services.length === 0
                  ? 'No services available'
                  : 'All services added'}
              </option>
            ) : (
              available.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.category}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={addService}
            disabled={available.length === 0}
            title="Add this service to the policy"
          >
            + Add
          </button>
        </div>
        <span className="field__hint">
          {serviceIds.length} service{serviceIds.length === 1 ? '' : 's'} granted.
          Changes apply when you save.
        </span>
      </div>
    </InlineForm>
  );
}
